import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Eye, EyeOff, Save, X, Upload, Image as ImageIcon, ArrowUp, ArrowDown } from "lucide-react";
import { API } from "@/lib/config";

// Reusable image uploader → returns a public /api/media/files URL
const ImageField = ({ value, onChange, testId }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/media/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      // res.data.url is like /api/media/files/... ; build absolute URL
      onChange(`${API.replace(/\/api$/, "")}${res.data.url}`);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        {value ? (
          <img src={value} alt="preview" className="h-16 w-16 rounded-lg object-cover border border-[#E5E5E5]" />
        ) : (
          <div className="h-16 w-16 rounded-lg bg-[#FFF5F9] flex items-center justify-center border border-dashed border-[#E5E5E5]"><ImageIcon className="h-5 w-5 text-[#C8A96A]" /></div>
        )}
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`${testId}-upload`} className="inline-flex items-center gap-1.5 border border-[#E5E5E5] text-[#4A4A4A] hover:bg-[#FFF5F9] rounded-full px-4 py-2 text-xs font-semibold">
          <Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload Image"}
        </button>
        {value && <button type="button" onClick={() => onChange("")} className="text-xs text-red-500 hover:underline">Remove</button>}
      </div>
      <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="…or paste an image URL" data-testid={`${testId}-url`} className="w-full bg-white border border-[#E5E5E5] rounded-lg px-3 py-2 text-xs" />
    </div>
  );
};

const emptyItem = (fields) => {
  const o = {};
  fields.forEach((f) => { o[f.key] = f.type === "number" ? 0 : ""; });
  o.status = "draft";
  return o;
};

// Generic CRUD manager for any CMS content type
export const ContentManager = ({ title, description, ctype, fields }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // item object or null
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/cms/${ctype}`);
      setItems(res.data);
    } catch (err) {
      toast.error(`Failed to load ${title}`);
    }
  };
  useEffect(() => { load(); }, [ctype]);

  const startNew = () => setEditing(emptyItem(fields));
  const startEdit = (item) => setEditing({ ...item });

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/cms/${ctype}/${editing.id}`, editing);
      } else {
        await axios.post(`${API}/cms/${ctype}`, editing);
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error("Save failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (item) => {
    const status = item.status === "published" ? "draft" : "published";
    try {
      await axios.put(`${API}/cms/${ctype}/${item.id}`, { status });
      toast.success(status === "published" ? "Published" : "Unpublished");
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete this ${title.replace(/s$/, "").toLowerCase()}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/cms/${ctype}/${item.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const move = async (item, dir) => {
    const idx = items.findIndex((i) => i.id === item.id);
    const swap = items[idx + dir];
    if (!swap) return;
    try {
      await Promise.all([
        axios.put(`${API}/cms/${ctype}/${item.id}`, { order: swap.order ?? (idx + dir) }),
        axios.put(`${API}/cms/${ctype}/${swap.id}`, { order: item.order ?? idx }),
      ]);
      load();
    } catch { toast.error("Reorder failed"); }
  };

  const inputCls = "w-full bg-white border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E94F9A]/30";

  return (
    <div className="space-y-5" data-testid={`content-manager-${ctype}`}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h4 className="font-serif text-lg font-bold text-[#4B1E5A]">{title}</h4>
          {description && <p className="font-sans text-xs text-[#4A4A4A]">{description}</p>}
        </div>
        <button onClick={startNew} data-testid={`add-${ctype}-btn`} className="inline-flex items-center gap-1.5 bg-[#E94F9A] text-white rounded-full px-4 py-2 text-xs font-semibold hover:bg-[#D13D86]">
          <Plus className="h-3.5 w-3.5" /> Add New
        </button>
      </div>

      {/* Editor */}
      {editing && (
        <div className="border border-[#E94F9A]/30 bg-[#FFF5F9]/20 rounded-2xl p-5 space-y-4" data-testid={`editor-${ctype}`}>
          <div className="flex items-center justify-between">
            <h5 className="font-serif font-bold text-[#4B1E5A]">{editing.id ? "Edit" : "New"} {title.replace(/s$/, "")}</h5>
            <button onClick={() => setEditing(null)} className="text-[#4A4A4A] hover:text-[#E94F9A]"><X className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map((f) => (
              <div key={f.key} className={f.full || f.type === "textarea" || f.type === "image" ? "md:col-span-2" : ""}>
                <label className="text-[11px] font-semibold text-[#4A4A4A] uppercase tracking-wider">{f.label}</label>
                {f.type === "textarea" ? (
                  <textarea rows={3} value={editing[f.key] || ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} data-testid={`field-${ctype}-${f.key}`} className={inputCls} placeholder={f.placeholder} />
                ) : f.type === "image" ? (
                  <ImageField value={editing[f.key]} onChange={(v) => setEditing({ ...editing, [f.key]: v })} testId={`field-${ctype}-${f.key}`} />
                ) : f.type === "number" ? (
                  <input type="number" value={editing[f.key] ?? 0} onChange={(e) => setEditing({ ...editing, [f.key]: parseInt(e.target.value) || 0 })} data-testid={`field-${ctype}-${f.key}`} className={inputCls} />
                ) : (
                  <input value={editing[f.key] || ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} data-testid={`field-${ctype}-${f.key}`} className={inputCls} placeholder={f.placeholder} />
                )}
                {f.hint && <p className="text-[10px] text-[#9b9b9b] mt-1">{f.hint}</p>}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={save} disabled={saving} data-testid={`save-${ctype}-btn`} className="inline-flex items-center gap-1.5 bg-[#4B1E5A] text-white rounded-full px-6 py-2.5 text-sm font-semibold hover:bg-[#E94F9A] disabled:opacity-60">
              <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
            </button>
            <label className="flex items-center gap-2 text-xs font-semibold text-[#4B1E5A]">
              <input type="checkbox" checked={editing.status === "published"} onChange={(e) => setEditing({ ...editing, status: e.target.checked ? "published" : "draft" })} data-testid={`publish-checkbox-${ctype}`} />
              Publish immediately
            </label>
          </div>
        </div>
      )}

      {/* List */}
      <div className="space-y-2">
        {items.length === 0 && <p className="text-sm text-[#4A4A4A] text-center py-8">No items yet. Click "Add New" to create one.</p>}
        {items.map((item, idx) => (
          <div key={item.id} data-testid={`${ctype}-row-${item.id}`} className="flex items-center justify-between gap-3 border border-[#E5E5E5] rounded-xl p-3">
            <div className="flex items-center gap-3 min-w-0">
              {(item.image || item.logo) && <img src={item.image || item.logo} alt="" className="h-10 w-10 rounded object-cover flex-shrink-0" />}
              <div className="min-w-0">
                <p className="font-serif text-sm font-bold text-[#4B1E5A] truncate">{item[fields[0].key] || "(untitled)"}</p>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${item.status === "published" ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"}`}>{item.status === "published" ? "Published" : "Draft"}</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button onClick={() => move(item, -1)} disabled={idx === 0} className="p-1.5 text-[#4A4A4A] hover:text-[#E94F9A] disabled:opacity-30" title="Move up"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button onClick={() => move(item, 1)} disabled={idx === items.length - 1} className="p-1.5 text-[#4A4A4A] hover:text-[#E94F9A] disabled:opacity-30" title="Move down"><ArrowDown className="h-3.5 w-3.5" /></button>
              <button onClick={() => togglePublish(item)} data-testid={`toggle-publish-${ctype}-${item.id}`} className="p-1.5 text-[#4A4A4A] hover:text-[#E94F9A]" title={item.status === "published" ? "Unpublish" : "Publish"}>
                {item.status === "published" ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </button>
              <button onClick={() => startEdit(item)} data-testid={`edit-${ctype}-${item.id}`} className="text-xs font-semibold text-[#4B1E5A] hover:text-[#E94F9A] px-2">Edit</button>
              <button onClick={() => remove(item)} data-testid={`delete-${ctype}-${item.id}`} className="p-1.5 text-red-500 hover:text-red-700" title="Delete"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
